import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import './ContactUs.css';
import SEO from './SEO.js';

const ContactUs = () => {
  const [status, setStatus] = useState("");

  const [formData, setFormData] = useState({
    type: '',
    subject: '',
    fullName: '',
    email: '',
    phone: '',
    message: '',
    timestamp: ''
  });

  const [errors, setErrors] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });

  const [validated, setValidated] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    show: false,
    type: '',
    message: ''
  });

  // Reset validation state when component mounts
  useEffect(() => {
    setValidated(false);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Validate field as user types
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let errorMessage = '';

    switch (name) {
      case 'fullName':
        if (!value.trim()) {
          errorMessage = 'Name is required';
        } else if (value.trim().length < 3) {
          errorMessage = 'Name must be at least 3 characters';
        }
        break;

      case 'email':
        if (!value.trim()) {
          errorMessage = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errorMessage = 'Please enter a valid email address';
        }
        break;

      case 'phone':
        if (value.trim() && !/^[0-9]{10}$/.test(value.replace(/[\s-]/g, ''))) {
          errorMessage = 'Please enter a valid 10-digit phone number';
        }
        break;

      case 'message':
        if (!value.trim()) {
          errorMessage = 'Message is required';
        } else if (value.trim().length < 10) {
          errorMessage = 'Message must be at least 10 characters';
        }
        break;

      default:
        break;
    }

    setErrors(prevErrors => ({
      ...prevErrors,
      [name]: errorMessage
    }));

    return !errorMessage;
  };

  const validateForm = () => {
    // Validate all fields
    const isFullNameValid = validateField('fullName', formData.fullName);
    const isEmailValid = validateField('email', formData.email);
    const isPhoneValid = validateField('phone', formData.phone);
    const isMessageValid = validateField('message', formData.message);

    return isFullNameValid && isEmailValid && isPhoneValid && isMessageValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setValidated(true);
    setStatus("Sending...");

    try {
      const payload = {
        ...formData,
        type: "message",        // or "services" / "equipment"
        subject: "Equiry", // you can make this dynamic if needed
        timestamp: new Date().getTime() // ensures uniqueness
      };
      const response = await fetch("https://script.google.com/macros/s/AKfycbxh0G5IHnJDGHNY9AyZakh0Mr9LWpPT4dO67qGJPsUAaMNXeKj1F4U75rQGBp83g8sRDg/exec", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setStatus("Message sent successfully!");
        setSubmitStatus({
          show: true,
          type: 'success',
          message: 'Thank you for contacting us! We will get back to you shortly.'
        });
        setFormData({
          type: '',
          subject: '',
          fullName: '',
          email: '',
          phone: '',
          message: '',
          timestamp: ''
        });
        setValidated(false);
      } else {
        setStatus("Error sending message.");
        setSubmitStatus({
          show: true,
          type: 'danger',
          message: 'There was an error sending your message. Please try again later.'
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setStatus("Error sending message.");
      setSubmitStatus({
        show: true,
        type: 'danger',
        message: 'There was an error sending your message. Please try again later.'
      });
    }
  };

  return (
    <div className="contact-section" id="contact">
      <SEO
        title="Contact Us - Caremetto Healthcare"
        description="Contact Caremetto Healthcare for home healthcare services in Bangalore. Call us at +91 9964037300 or visit our office in Kasturi Nagar."
        keywords="contact Caremetto, healthcare contact Bangalore, book nurse Bangalore, home care enquiry, healthcare phone number"
      />
      <Container>
        <div className="section-header text-center">
          <h2>Contact Us</h2>
          <p>Get in touch with our team for any inquiries or assistance</p>
        </div>

        <Row className="contact-content">
          <Col lg={5} className="mb-4 mb-lg-0">
            <div className="contact-info">
              <h3>Get In Touch</h3>
              <p>We're here to help you with any questions about our services or to schedule an appointment.</p>

              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="fas fa-map-marker-alt"></i>
                  </div>
                  <div className="contact-text">
                    <h4>Our Location</h4>
                    <p>C K Palya Main Road ,<br />Basavanapura, Mandi Layout , <br />Bengaluru Karnataka 560083</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="fas fa-phone-alt"></i>
                  </div>
                  <div className="contact-text">
                    <h4>Phone Number</h4>
                    <p>+91 8709530016</p>
                    <p>+91 9964037300</p>

                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="fas fa-envelope"></i>
                  </div>
                  <div className="contact-text">
                    <h4>Email Address</h4>
                    <p>info@caremetto.in</p>
                    {/* <p>support@caremetto.com</p> */}
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="contact-text">
                    <h4>Working Hours</h4>
                    <p>Monday - Friday: 8:00 AM - 8:00 PM</p>
                    <p>Saturday - Sunday: 9:00 AM - 6:00 PM</p>
                  </div>
                </div>
              </div>

              <div className="social-links mt-4">
                <a href="#" className="social-link"><i className="fab fa-facebook-f"></i></a>
                <a href="#" className="social-link"><i className="fab fa-twitter"></i></a>
                <a href="#" className="social-link"><i className="fab fa-instagram"></i></a>
                <a href="#" className="social-link"><i className="fab fa-linkedin-in"></i></a>
              </div>
            </div>
          </Col>

          <Col lg={7}>
            <Card className="contact-form-card">
              <Card.Body>
                <h3>Send Us a Message</h3>
                {submitStatus.show && (
                  <Alert variant={submitStatus.type} onClose={() => setSubmitStatus({ show: false, type: '', message: '' })} dismissible>
                    {submitStatus.message}
                  </Alert>
                )}
                <Form className="contact-form" noValidate validated={validated} onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Full Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter your name"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          isInvalid={!!errors.fullName}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.fullName || 'Please provide your name.'}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Enter your email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          isInvalid={!!errors.email}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email || 'Please provide a valid email address.'}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          placeholder="Enter your phone number"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          isInvalid={!!errors.phone}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.phone}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    {/* <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Subject</Form.Label>
                        <Form.Control 
                          type="text" 
                          placeholder="Enter subject" 
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col> */}
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      placeholder="Enter your message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      isInvalid={!!errors.message}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.message || 'Please enter your message.'}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <div className="text-center mt-4">
                    <Button type="submit" className="submit-btn">Send Message</Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="map-section mt-5">
          <h3 className="text-center mb-4">Find Us On Map</h3>
          <div className="google-map">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1523.7910674086952!2d77.59355084317264!3d12.84092958471166!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sin!4v1777023921370!5m2!1sen!2sin"

              allowfullscreen=""
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              title="Google Maps"
            ></iframe>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ContactUs;